import { useCallback, useContext, useMemo, useEffect } from 'react';
import type { Chat } from '@trinity/chat';
import { ChatFileUploadStatus, FileSocketEvent  } from '@trinity/chat';
import { useFileUploadProgress } from '@trinity/chat-components/containers/WorkspaceFileUpload/hooks';
import { useFileSocket } from '../../../../../packages/chat-components/src/websocket/hooks/useFileSocket';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { FileUploadItem } from './FileUpload.File';

type Props = {
  className?: string;
  uploadIdentifier: string;
  value: Chat.FileUploadItem;
};

export const FileUploadFileContainer = ({ className, uploadIdentifier, value }: Props) => {

  const pctComplete = useFileUploadProgress(value.identifier);

  useFileReadySocketEvent(value, uploadIdentifier);

  return (
    <FileUploadItem
      className={className}
      fileId={value.fileId}
      name={value.name}
      status={value.status}
      pctComplete={pctComplete} />
  );
};

const useFileReadySocketEvent = (value: Chat.FileUploadItem, uploadIdentifier: string) => {
  const dispatch = useContext(ChatStateDispatchContext);

  const socket = useFileSocket();

  const handleFileAvailable = useCallback((event: { fileIdentifier: string }) => {
    dispatch({
      type: 'file-upload-chat-ready',
      payload: {
        identifier: uploadIdentifier,
        fileIdentifier: event.fileIdentifier,
      },
    });
  }, [dispatch, uploadIdentifier]);

  const enabled = useMemo(() => {
    return value.status === ChatFileUploadStatus.Uploaded &&
      !!value.fileId;
  }, [value.fileId, value.status]);

  useEffect(() => {
    if (enabled && socket) {
      socket.on(FileSocketEvent.ChatAvailable, handleFileAvailable);
      return () => {
        socket.off(FileSocketEvent.ChatAvailable, handleFileAvailable);
      };
    }
  }, [enabled, handleFileAvailable, socket]);

  useEffect(() => {
    if (enabled && socket) {
      socket.emit('subscribe', value.fileId);
      socket.open();

      return () => {
        socket.emit('unsubscribe', value.fileId);
        socket.close();
      };
    }
  }, [enabled, socket, value.fileId]);
};